/**
 * ST-05 (AIVA-6805) — Ctrl/Cmd+Shift+K toggles "All actions" while focus is in
 * the AiVA pane: the same key Deckorate's palette has always used, now
 * answered by the pane so it works on every tab, with Deckorate on or off.
 *
 * Not Ctrl+Space (the spec's key): Microsoft's add-in platform blocks it —
 * "When the keyboard focus is inside the add-in UI, Ctrl+Space and
 * Ctrl+Shift+F10 won't work as these are essential accessibility shortcuts",
 * and a custom shortcut's key must be A–Z, 0–9, "-", "_" or "+".
 * https://learn.microsoft.com/en-us/office/dev/add-ins/design/keyboard-shortcuts
 *
 * Capture phase on `document`, like Code Mode's Alt+Shift+K in TaskPane: a
 * bubble-phase listener is the last to see the keydown and is lost to anything
 * upstream that stops propagation.
 */

import { useEffect } from "react";

type ShortcutKeys = Pick<KeyboardEvent, "key" | "ctrlKey" | "metaKey" | "altKey" | "shiftKey">;

export function isPaletteShortcut(event: ShortcutKeys): boolean {
  return (event.ctrlKey || event.metaKey) && event.shiftKey && !event.altKey && event.key.toLowerCase() === "k";
}

export function usePaletteShortcut(
  enabled: boolean,
  onToggle: () => void,
  /** True while the pane must not react to keys (the AI notice sheet is up). */
  isBlocked: () => boolean,
): void {
  useEffect(() => {
    if (!enabled) return;
    function handleKeyDown(event: KeyboardEvent) {
      if (!isPaletteShortcut(event) || isBlocked()) return;
      event.preventDefault();
      event.stopPropagation();
      onToggle();
    }
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [enabled, onToggle, isBlocked]);
}
